import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, Button, Input } from '../components/ui'
import { PageHeader } from '../../ui'
import { supabase } from '../../services/supabase'
import {
  adminListUsers,
  adminListSites,
  adminListInvitations,
  adminCreateInvitation,
  adminCreateSite,
  adminUpdateUser,
  adminDeleteInvitation,
} from '../lib/ixverifyApi'
import { useIXVerifyAuth } from '../contexts'
import type { User, Site, UserRole, InvitationWithSite } from '../types'

interface UserWithSite extends User {
  site?: Site | null
}

type TabType = 'users' | 'sites' | 'invitations' | 'stats'

export function SuperAdmin() {
  const { user: currentUser, loading: authLoading } = useIXVerifyAuth()
  const [activeTab, setActiveTab] = useState<TabType>('users')
  const [users, setUsers] = useState<UserWithSite[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [invitations, setInvitations] = useState<InvitationWithSite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showSiteModal, setShowSiteModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserWithSite | null>(null)

  // Form states
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer')
  const [inviteSiteId, setInviteSiteId] = useState('')
  const [siteName, setSiteName] = useState('')
  const [siteAddress, setSiteAddress] = useState('')
  const [saving, setSaving] = useState(false)

  // Create user form
  const [createEmail, setCreateEmail] = useState('')
  const [createPassword, setCreatePassword] = useState('')
  const [createFullName, setCreateFullName] = useState('')
  const [createRole, setCreateRole] = useState<UserRole>('viewer')
  const [createSiteId, setCreateSiteId] = useState('')

  // Edit user form
  const [editFullName, setEditFullName] = useState('')
  const [editRole, setEditRole] = useState<UserRole>('viewer')
  const [editSiteId, setEditSiteId] = useState('')

  const isSuperAdmin = currentUser?.role === 'superadmin'

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return

    if (isSuperAdmin) {
      fetchData()
    } else {
      setLoading(false)
    }
  }, [isSuperAdmin, authLoading])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Fetch all users with site info
      const { data: usersData, error: usersError } = await adminListUsers()

      if (usersError) throw usersError

      // Fetch all sites
      const { data: sitesData, error: sitesError } = await adminListSites()

      if (sitesError) throw sitesError

      // Fetch pending invitations
      const { data: invitationsData, error: invitationsError } = await adminListInvitations({ pending: true })

      if (invitationsError) {
        console.error('Error fetching invitations:', invitationsError)
        // Don't throw - invitations table might not exist yet
      }

      setUsers(usersData || [])
      setSites(sitesData || [])
      setInvitations(invitationsData || [])
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setSaving(true)
    setError('')

    try {
      // Check if user already exists
      const existingUser = users.find(u => u.email.toLowerCase() === inviteEmail.toLowerCase())
      if (existingUser) {
        setError('A user with this email already exists')
        setSaving(false)
        return
      }

      // Create invitation record
      const { data: invitation, error: inviteError } = await adminCreateInvitation({
        email: inviteEmail.trim().toLowerCase(),
        site_id: inviteSiteId || null,
        role: inviteRole,
      })

      if (inviteError) throw inviteError

      // Send invitation email via Edge Function
      const siteNameForEmail = sites.find(s => s.id === inviteSiteId)?.name || 'Drug Verify'
      const { error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: inviteEmail.trim(),
          inviterName: currentUser?.full_name || 'Administrator',
          siteName: siteNameForEmail,
          role: inviteRole,
          token: invitation.token,
        }
      })

      if (emailError) {
        console.error('Email error:', emailError)
        // Don't throw - invitation was created, email just didn't send
      }

      setInvitations([invitation, ...invitations])
      setShowInviteModal(false)
      setInviteEmail('')
      setInviteRole('viewer')
      setInviteSiteId('')
    } catch (err) {
      console.error('Error sending invite:', err)
      setError('Failed to send invitation')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateSite = async () => {
    if (!siteName.trim()) {
      setError('Site name is required')
      return
    }

    setSaving(true)
    setError('')

    try {
      const cleanName = siteName.trim()
      const cleanAddress = siteAddress.trim() || null

      const { data, error: siteError } = await adminCreateSite({ name: cleanName, address: cleanAddress })

      if (siteError) {
        console.error('Site create error:', siteError)
        setError(`Failed to create site: ${siteError.message}`)
        setSaving(false)
        return
      }

      setSites([...sites, data].sort((a, b) => a.name.localeCompare(b.name)))
      setShowSiteModal(false)
      setSiteName('')
      setSiteAddress('')
    } catch (err: any) {
      console.error('Caught error:', err)
      setError(`Failed to create site: ${err?.message || 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  const openEditUser = (user: UserWithSite) => {
    setEditingUser(user)
    setEditFullName(user.full_name || '')
    setEditRole(user.role || 'viewer')
    setEditSiteId(user.site_id || '')
    setShowEditUserModal(true)
    setError('')
  }

  const handleUpdateUser = async () => {
    if (!editingUser) return

    setSaving(true)
    setError('')

    try {
      // NOTE: site_id is managed via user_site_access, not the users table; the
      // API only persists full_name + role (site_id is ignored server-side).
      const { error: updateError } = await adminUpdateUser(editingUser.id, {
        full_name: editFullName.trim() || null,
        role: editRole,
      })

      if (updateError) throw updateError

      // Update local state
      setUsers(users.map(u =>
        u.id === editingUser.id
          ? {
              ...u,
              full_name: editFullName.trim() || null,
              role: editRole,
              site_id: editSiteId || null,
              site: sites.find(s => s.id === editSiteId) || null,
            }
          : u
      ))
      setShowEditUserModal(false)
      setEditingUser(null)
    } catch (err) {
      console.error('Error updating user:', err)
      setError('Failed to update user')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      const { error } = await adminDeleteInvitation(invitationId)

      if (error) throw error

      setInvitations(invitations.filter(i => i.id !== invitationId))
    } catch (err) {
      console.error('Error deleting invitation:', err)
    }
  }

  const handleCreateUser = async () => {
    if (!createEmail.trim() || !createEmail.includes('@')) {
      setError('Please enter a valid email address')
      return
    }
    if (!createPassword || createPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (!createFullName.trim()) {
      setError('Full name is required')
      return
    }

    setSaving(true)
    setError('')

    try {
      // Check if user already exists
      const existingUser = users.find(u => u.email.toLowerCase() === createEmail.toLowerCase())
      if (existingUser) {
        setError('A user with this email already exists')
        setSaving(false)
        return
      }

      // Call the Edge Function to create the user
      const { data, error: createError } = await supabase.functions.invoke('create-user', {
        body: {
          email: createEmail.trim().toLowerCase(),
          password: createPassword,
          fullName: createFullName.trim(),
          role: createRole,
          siteId: createSiteId || null,
          createdBy: currentUser?.id,
        }
      })

      console.log('Edge function response:', { data, createError })

      if (createError) {
        console.error('Create user error:', createError)
        // Try to get the actual error from the response
        const errorMessage = data?.error || createError.message || 'Failed to create user'
        throw new Error(errorMessage)
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create user')
      }

      // Add the new user to the local state
      const newUser: UserWithSite = {
        id: data.user.id,
        email: data.user.email,
        full_name: data.user.fullName,
        role: data.user.role,
        site_id: data.user.siteId,
        site: sites.find(s => s.id === data.user.siteId) || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        avatar_url: null,
      }

      setUsers([newUser, ...users])
      setShowCreateUserModal(false)
      setCreateEmail('')
      setCreatePassword('')
      setCreateFullName('')
      setCreateRole('viewer')
      setCreateSiteId('')
    } catch (err: any) {
      console.error('Error creating user:', err)
      setError(err.message || 'Failed to create user')
    } finally {
      setSaving(false)
    }
  }

  const roleColors: Record<UserRole, string> = {
    superadmin: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    admin: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    coordinator: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    viewer: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  }

  const roleLabels: Record<UserRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Admin',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Not a superadmin
  if (!isSuperAdmin) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="Super Admin" subtitle="System-wide administration" />
        <Card>
          <CardContent className="p-6 text-center">
            <svg className="w-12 h-12 mx-auto text-red-500 dark:text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-gray-900 dark:text-gray-100 font-semibold mb-2">Access Denied</p>
            <p className="text-gray-600 dark:text-gray-400">
              You need super administrator privileges to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Super Admin Dashboard"
        subtitle="Manage all sites, users, and system settings"
        actions={
          <>
          <Button size="sm" onClick={() => setShowCreateUserModal(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Create User
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowInviteModal(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Invite User
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSiteModal(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Add Site
          </Button>
          </>
        }
      />

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{users.length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{sites.length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Sites</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{invitations.length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Pending Invites</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {users.filter(u => u.role === 'admin' || u.role === 'superadmin').length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Admins</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {(['users', 'sites', 'invitations'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'invitations' && invitations.length > 0 && (
                <span className="ml-2 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 text-xs px-2 py-0.5 rounded-full">
                  {invitations.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      ) : (
        <>
          {/* Users Tab */}
          {activeTab === 'users' && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">All Users ({users.length})</h2>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b">
                        <th className="pb-3 font-medium">User</th>
                        <th className="pb-3 font-medium">Role</th>
                        <th className="pb-3 font-medium">Site</th>
                        <th className="pb-3 font-medium">Joined</th>
                        <th className="pb-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {user.full_name || '-'}
                                {user.id === currentUser?.id && (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(you)</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[user.role || 'viewer']}`}>
                              {roleLabels[user.role || 'viewer']}
                            </span>
                          </td>
                          <td className="py-3 text-gray-600 dark:text-gray-400">
                            {user.site?.name || <span className="text-gray-400 dark:text-gray-500">No site</span>}
                          </td>
                          <td className="py-3 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="py-3 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditUser(user)}
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sites Tab */}
          {activeTab === 'sites' && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">All Sites ({sites.length})</h2>
              </CardHeader>
              <CardContent>
                {sites.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">No sites created yet</p>
                    <Button onClick={() => setShowSiteModal(true)}>Create First Site</Button>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {sites.map((site) => {
                      const siteUsers = users.filter(u => u.site_id === site.id)
                      return (
                        <Card key={site.id} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{site.name}</h3>
                                {site.address && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">{site.address}</p>
                                )}
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {siteUsers.length} users
                              </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-1">
                              {siteUsers.slice(0, 3).map(u => (
                                <span key={u.id} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded">
                                  {u.full_name || u.email}
                                </span>
                              ))}
                              {siteUsers.length > 3 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{siteUsers.length - 3} more
                                </span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Invitations Tab */}
          {activeTab === 'invitations' && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">Pending Invitations ({invitations.length})</h2>
              </CardHeader>
              <CardContent>
                {invitations.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">No pending invitations</p>
                    <Button onClick={() => setShowInviteModal(true)}>Send an Invite</Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {invitations.map((invitation) => (
                      <div key={invitation.id} className="py-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{invitation.email}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[invitation.role]}`}>
                              {roleLabels[invitation.role]}
                            </span>
                            <span>{invitation.site?.name || 'No site'}</span>
                            <span>Expires {formatDate(invitation.expires_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteInvitation(invitation.id)}
                          className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          Cancel
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowInviteModal(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Invite New User</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as UserRole)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assign to Site
                  </label>
                  <select
                    value={inviteSiteId}
                    onChange={(e) => setInviteSiteId(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="">No site assigned</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>

                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSendInvite} disabled={saving} className="flex-1">
                    {saving ? 'Sending...' : 'Send Invitation'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowInviteModal(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateUserModal(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Create New User</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Create a user directly without email verification. You'll set their password.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={createFullName}
                    onChange={(e) => setCreateFullName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password *
                  </label>
                  <Input
                    type="password"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    placeholder="Min 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    value={createRole}
                    onChange={(e) => setCreateRole(e.target.value as UserRole)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assign to Site
                  </label>
                  <select
                    value={createSiteId}
                    onChange={(e) => setCreateSiteId(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="">No site assigned</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>

                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleCreateUser} disabled={saving} className="flex-1">
                    {saving ? 'Creating...' : 'Create User'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateUserModal(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Site Modal */}
      {showSiteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSiteModal(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Create New Site</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Site Name *
                  </label>
                  <Input
                    type="text"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    placeholder="e.g., Main Hospital"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Address (optional)
                  </label>
                  <Input
                    type="text"
                    value={siteAddress}
                    onChange={(e) => setSiteAddress(e.target.value)}
                    placeholder="123 Medical Center Dr"
                  />
                </div>

                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleCreateSite} disabled={saving} className="flex-1">
                    {saving ? 'Creating...' : 'Create Site'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowSiteModal(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditUserModal(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Edit User</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <Input type="email" value={editingUser.email} disabled className="bg-gray-50 dark:bg-gray-900/40" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    value={editFullName}
                    onChange={(e) => setEditFullName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value as UserRole)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Site
                  </label>
                  <select
                    value={editSiteId}
                    onChange={(e) => setEditSiteId(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="">No site assigned</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>

                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleUpdateUser} disabled={saving} className="flex-1">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowEditUserModal(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
